import React, { useState, useEffect } from 'react';

interface WorkwearPurchaseProps {
  userId: string;
  profileId: string;
  onNext: () => void;
  onSkip: () => void;
}

const WorkwearPurchase: React.FC<WorkwearPurchaseProps> = ({ userId, profileId, onNext, onSkip }) => {
  const [selectedSize, setSelectedSize] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [kitConfig, setKitConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKitConfig();
  }, []);

  const fetchKitConfig = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-workwear-kit-config`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ kit_type: 'registration_kit' })
        }
      );

      const data = await response.json();
      if (data.error) throw new Error(data.error);
      
      setKitConfig(data);
    } catch (error) {
      console.error('Error fetching kit config:', error);
      setError('Failed to load kit information');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedSize) {
      setError('Please select a size');
      return;
    }

    if (!kitConfig) {
      setError('Kit configuration not loaded');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-workwear-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            userId,
            profileId,
            size: selectedSize,
            price: kitConfig.total_price_cents,
            successUrl: `${window.location.origin}/provider-signup?step=6&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/provider-signup?step=5`
          }),
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Payment URL not received');
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleSkip = () => {
    if (window.confirm('Are you sure you want to skip the workwear purchase? You can order it later from your dashboard.')) {
      onSkip();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-6xl text-teal-600 mb-4"></i>
          <p className="text-gray-600">Loading kit information...</p>
        </div>
      </div>
    );
  }

  if (!kitConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500 mb-4"></i>
          <p className="text-gray-600 mb-4">Failed to load kit information</p>
          <button
            onClick={fetchKitConfig}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#FF6A00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-shirt-fill text-[#FF6A00] text-4xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Get Your Starter Workwear Kit</h1>
          <p className="text-lg text-gray-600">Professional branded uniform to represent Taskbudy</p>
        </div>

        {/* Mandatory Notice */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-2xl shadow-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-3xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Workwear Purchase Required</h2>
              <p className="text-white/90 text-lg mb-3">
                All Taskbudy providers must wear branded workwear to maintain our professional standards and build customer trust.
              </p>
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-sm text-white/80 mb-2">
                  <strong>Why it's mandatory:</strong>
                </p>
                <ul className="space-y-1 text-sm text-white/80">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line"></i>
                    Professional brand representation
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line"></i>
                    Instant customer recognition and trust
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line"></i>
                    Quality assurance for clients
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Starter Kit */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">{kitConfig.kit_name}</h3>
              <p className="text-gray-600">{kitConfig.kit_description}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">One-time purchase</div>
              <div className="text-3xl font-bold text-teal-600">£{kitConfig.base_price.toFixed(2)} + VAT</div>
              <div className="text-sm text-gray-600 mt-1">Total: <span className="font-semibold">£{kitConfig.total_price.toFixed(2)}</span> inc. VAT</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Kit Details */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-6">
                <div className="relative w-full h-64 rounded-xl overflow-hidden mb-6">
                  <img
                    src="https://public.readdy.ai/ai/img_res/032d31f6deb95fb5a8006d32a91c3fbc.jpg"
                    alt="Workwear Kit"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{kitConfig.kit_name}</h2>
                <p className="text-gray-600 mb-6">
                  Everything you need to look professional and represent the Taskbudy brand
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600"></i>
                  What's Included:
                </h3>
                <ul className="space-y-3">
                  {kitConfig.items.map((item: any, index: number) => (
                    <li key={index} className="flex items-center gap-3 text-gray-700">
                      <i className="ri-check-line text-teal-600 text-xl"></i>
                      <span>{item.name} {item.quantity > 1 && `(${item.quantity}x)`}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-fill text-blue-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-bold text-gray-900 text-sm mb-1">Free Delivery</p>
                    <p className="text-xs text-gray-600">Your kit will be delivered within 5-7 working days</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Purchase Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Your Size</h2>

              {error && (
                <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-800 flex items-center gap-2">
                    <i className="ri-error-warning-fill text-lg"></i>
                    {error}
                  </p>
                </div>
              )}

              <div className="mb-8">
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Uniform Size <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {kitConfig.available_sizes.map((size: string) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => {
                        setSelectedSize(size);
                        setError('');
                      }}
                      className={`py-3 px-4 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap ${
                        selectedSize === size
                          ? 'bg-[#0B3C5D] text-white border-2 border-[#0B3C5D]'
                          : 'bg-gray-100 text-gray-700 border-2 border-gray-200 hover:border-[#0B3C5D]/30'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Summary */}
              <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-teal-600"></i>
                  Payment Summary
                </h4>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Starter Kit</span>
                    <span className="font-semibold text-gray-800">£{kitConfig.base_price.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">VAT (20%)</span>
                    <span className="font-semibold text-gray-800">£{kitConfig.vat_amount.toFixed(2)}</span>
                  </div>

                  <div className="border-t-2 border-teal-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">Total Due</span>
                      <span className="text-2xl font-bold text-teal-600">£{kitConfig.total_price.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase Button */}
              <button
                type="button"
                onClick={handlePurchase}
                disabled={isProcessing}
                className="w-full py-4 bg-gradient-to-r from-[#FF6A00] to-[#E55F00] text-white rounded-xl hover:from-[#FF6A00]/90 hover:to-[#E55F00]/90 transition-all duration-300 font-bold text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none mb-4"
              >
                {isProcessing ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-shopping-cart-line text-xl"></i>
                    <span>Purchase Kit - £{kitConfig.total_price.toFixed(2)}</span>
                  </>
                )}
              </button>

              {/* Skip Button */}
              <button
                type="button"
                onClick={handleSkip}
                disabled={isProcessing}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Skip for Now (Order Later)
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                You can order your workwear kit anytime from your provider dashboard
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkwearPurchase;
